import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Search,
  Wallet,
  ShieldCheck,
  Wrench,
  RefreshCcw,
  Headphones,
  ChevronRight,
  TrendingDown,
  Building2,
  Clock4,
  PiggyBank,
  PhoneCall,
} from 'lucide-react';

/**
 * NOTE FOR FUTURE EDITS:
 * This section advertises **10.45% starting** for the used-car loan — the
 * canonical rate as of 2026-07-29. Products.jsx, FinancialServices.jsx and
 * translations.js were all reconciled to this rate in the same session.
 * Source of truth: WhatsApp poster shared on 06 May 2026.
 *
 * SAVINGS MATH (must total ≈ ₹1,00,000):
 *   Loan processing fee   ₹15,000  →  ₹5,000   = ₹10,000
 *   RTO transfer          ₹5,000   →  Free     = ₹5,000
 *   Documentation         ₹3,000   →  Free     = ₹3,000
 *   Engine repair         ₹75,000  →  Free     = ₹75,000  (covered by free warranty)
 *   First service (PMS)   ₹8,000   →  Free     = ₹8,000
 *   EMI during repair     Self     →  We pay   = bonus protection
 *                                              ─────────
 *                                              ₹1,01,000  ≈ "Up to ₹1,00,000"
 */

// Partner banks — pure typographic chips (no external logo CDN).
const BANKS = [
  'Bajaj Finserv',
  'IDFC First',
  'Axis Bank',
  'HDFC Bank',
  'ICICI Bank',
  'Bandhan Bank',
  'Shriram Finance',
  'Kotak Mahindra',
];

// The 4-stage ownership ladder. Each stage = one phase in the customer's journey.
const LADDER = [
  {
    icon: Search,
    phase: 'Pre-Purchase',
    title: 'AI Inspection',
    line: '200+ point AI inspection · OBD-2 diagnostics · RTO verified',
    saved: 'Avoid hidden defects',
  },
  {
    icon: Wallet,
    phase: 'Purchase',
    title: 'Loan @ 10.45%',
    line: 'Same-day approval · Low CIBIL accepted · 1% processing · EMI paused if engine repair keeps your car in workshop',
    saved: 'Saves up to ₹13,000',
  },
  {
    icon: ShieldCheck,
    phase: 'Early Ownership',
    title: 'Free Engine Warranty',
    line: '180 days · Up to ₹10 Lakh cover · OEM-grade workshops',
    saved: 'Saves ₹75,000',
  },
  {
    icon: Wrench,
    phase: 'Maintenance',
    title: 'Free First Service',
    line: 'Doorstep PMS · OEM parts · 1,000 km warranty',
    saved: 'Saves ₹8,000',
  },
  {
    icon: PiggyBank,
    phase: 'Ongoing Service',
    title: 'Up to 50 % off on Maintenance',
    line: 'Lifetime member rates on routine service, parts and labour at OEM-grade workshops',
    saved: 'Saves 40–50 % every visit',
  },
  {
    icon: PhoneCall,
    phase: 'Concierge',
    title: 'Insurance Claim Assistance',
    line: 'One number for any issue with your car — claims, breakdowns, RSA, paperwork',
    saved: 'Single point of contact',
  },
];

// Comparison ledger — exact figures from the founder's collateral.
// Total savings tuned to ≈ ₹1,00,000 (see math note at top of file).
const LEDGER = [
  { label: 'Loan processing fee (2–3% → 1%)',           other: '₹ 15,000',  us: '₹ 5,000' },
  { label: 'RTO transfer charges',                      other: '₹ 5,000',   us: 'Free' },
  { label: 'Documentation charges',                     other: '₹ 3,000',   us: 'Free' },
  { label: 'Engine repair expenses (180-day · ₹10L)',   other: '₹ 75,000',  us: 'Free' },
  { label: 'First general service (PMS)',               other: '₹ 8,000',   us: 'Free' },
  { label: 'EMI during engine repair downtime',         other: 'Self-paid', us: 'We pay' },
  { label: 'Insurance renewal (up to 20 % off)',        other: '₹ 0',       us: 'Free' },
];

// Investor-tilt KPIs — small, quiet, factual. Hidden in plain sight.
const KPIS = [
  { value: '8', label: 'Banking Partners', icon: Building2 },
  { value: '24×7', label: 'Loan Support', icon: Headphones },
  { value: '< 1 Hr', label: 'Same-Day Approval', icon: Clock4 },
  { value: '20 %', label: 'Insurance Discount', icon: TrendingDown },
];

const OwnershipEcosystem = ({ onApplyLoan }) => {
  return (
    <section
      id="ownership-ecosystem"
      className="relative py-20 lg:py-28 bg-slate-950 text-white overflow-hidden"
      data-testid="ownership-ecosystem-section"
    >
      {/* Architectural texture overlay — investor-grade premium feel */}
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1649608118930-70b0a62cdf62?crop=entropy&cs=srgb&fm=jpg&w=1600&q=60)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      {/* Subtle gradient accents */}
      <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[480px] h-[480px] rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-300">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            The Wisedrive Ecosystem
          </span>
          <h2
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-white leading-[1.05]"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            ₹1,00,000 saved
            <br className="hidden sm:inline" />
            <span className="text-slate-400">
              {' '}from search to ownership.
            </span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl">
            Buying an inspection doesn't just protect your purchase. It unlocks
            financing at 10.45 %, a free 180-day engine warranty (up to ₹10 Lakh),
            doorstep service and insurance discounts — and if a covered repair
            keeps your car in the workshop, <span className="text-emerald-300 font-semibold">we pay your EMI</span> until
            it's back on the road.
          </p>
        </div>

        {/* Partner banks — institutional trust, up front */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.4 }}
          className="mt-10 lg:mt-14"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500 mb-4">
            8 lending partners — and counting
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
            {BANKS.map((b) => (
              <span
                key={b}
                className="px-3.5 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm text-sm font-semibold text-slate-200 hover:border-white/25 hover:text-white transition-colors"
                data-testid={`ecosystem-bank-${b.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {b}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Bento grid — Ladder (left, 7-cols) + Ledger (right, 5-cols) */}
        <div className="mt-10 lg:mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* LEFT: Ownership Ladder (4-step journey) */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.5 }}
            className="col-span-1 md:col-span-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-6 lg:p-8"
            data-testid="ecosystem-ladder"
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500 mb-1">
              Lifecycle bundle
            </div>
            <h3
              className="text-2xl font-semibold text-white mb-6"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              One inspection. Six ways your money is protected.
            </h3>

            <ol className="relative space-y-5">
              {/* Vertical tracing line */}
              <div className="absolute left-[19px] top-2 bottom-2 w-px bg-gradient-to-b from-blue-500/60 via-white/10 to-emerald-500/60" />
              {LADDER.map((step, i) => {
                const Icon = step.icon;
                return (
                  <motion.li
                    key={step.title}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i * 0.08 }}
                    className="relative flex gap-4 group"
                  >
                    <div className="relative z-10 shrink-0 w-10 h-10 rounded-full bg-slate-900 border border-white/15 inline-flex items-center justify-center group-hover:border-emerald-400/50 transition-colors">
                      <Icon
                        className="h-4 w-4 text-blue-400 group-hover:text-emerald-400 transition-colors"
                        strokeWidth={1.8}
                      />
                    </div>
                    <div className="flex-1 pt-0.5">
                      <div className="flex flex-wrap items-baseline gap-2 mb-1">
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                          {step.phase}
                        </div>
                        <div className="text-base font-semibold text-white">
                          {step.title}
                        </div>
                      </div>
                      <div className="text-sm text-slate-400 leading-relaxed">
                        {step.line}
                      </div>
                      <div className="mt-1.5 text-xs font-bold text-emerald-400">
                        {step.saved}
                      </div>
                    </div>
                  </motion.li>
                );
              })}
            </ol>
          </motion.div>

          {/* RIGHT: Comparison Ledger */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="col-span-1 md:col-span-5 relative rounded-2xl border border-emerald-400/30 bg-white/5 backdrop-blur-xl p-6 lg:p-8 shadow-[0_0_40px_-8px_rgba(16,185,129,0.25)]"
            data-testid="ecosystem-ledger"
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-emerald-300 mb-1">
              Net benefit · per customer
            </div>
            <h3
              className="text-2xl font-semibold text-white mb-1"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              Other channel{' '}
              <span className="text-slate-500 font-normal">vs</span>{' '}
              Wisedrive
            </h3>
            <div className="text-xs text-slate-500 mb-6">
              All charges typical for a CIBIL 500+ used-car loan, FY26.
            </div>

            <div className="space-y-3">
              {LEDGER.map((row) => (
                <div
                  key={row.label}
                  className="grid grid-cols-12 gap-2 items-center text-sm py-2 border-b border-white/5 last:border-b-0"
                >
                  <div className="col-span-7 text-slate-300 leading-snug">
                    {row.label}
                  </div>
                  <div className="col-span-2 text-right font-mono text-rose-300/80 line-through decoration-rose-300/40">
                    {row.other}
                  </div>
                  <div className="col-span-3 text-right font-mono font-bold text-emerald-400">
                    {row.us}
                  </div>
                </div>
              ))}
            </div>

            {/* Net benefit row — emphasised */}
            <div className="mt-5 pt-5 border-t-2 border-emerald-400/30 grid grid-cols-12 gap-2 items-center">
              <div className="col-span-6 text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
                Net benefit
              </div>
              <div className="col-span-2 text-right font-mono text-rose-300/80 line-through decoration-rose-300/40 text-sm">
                ₹ 0
              </div>
              <div className="col-span-4 text-right">
                <div className="font-mono text-2xl sm:text-3xl font-black text-emerald-400 tracking-tighter leading-none">
                  ₹1,00,000
                </div>
                <div className="text-[10px] text-emerald-300/70 mt-0.5">
                  upto · saved
                </div>
              </div>
            </div>

            {/* EMI Protection callout — explains the safety net for loan customers */}
            <div className="mt-5 rounded-xl border border-blue-400/30 bg-blue-500/[0.07] p-4">
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-9 h-9 rounded-lg bg-blue-500/15 inline-flex items-center justify-center">
                  <ShieldCheck className="h-4.5 w-4.5 text-blue-300" strokeWidth={1.8} />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-blue-300 mb-1">
                    Loan-customer protection
                  </div>
                  <div className="text-sm font-semibold text-white leading-snug mb-1">
                    Your car is in the workshop. We pay your EMI.
                  </div>
                  <div className="text-xs text-slate-400 leading-relaxed">
                    If a covered engine claim parks your car for up to{' '}
                    <span className="text-white font-semibold">2 months</span>,
                    Wisedrive pays the EMI directly to your lender — your CIBIL stays
                    clean and your bank account untouched. Available only on cars
                    financed through Wisedrive.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* KPI ticker — investor signals, factual, quiet */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.4 }}
          className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3"
          data-testid="ecosystem-kpis"
        >
          {KPIS.map((k) => {
            const Icon = k.icon;
            return (
              <div
                key={k.label}
                className="flex items-center gap-3 px-5 py-4 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm hover:border-white/20 transition-colors"
              >
                <Icon className="h-5 w-5 text-blue-400 shrink-0" strokeWidth={1.6} />
                <div>
                  <div
                    className="font-mono text-2xl font-black text-white tracking-tighter leading-none"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    {k.value}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 mt-1">
                    {k.label}
                  </div>
                </div>
              </div>
            );
          })}
        </motion.div>

        {/* Dual CTAs */}
        <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center">
          <button
            onClick={onApplyLoan}
            data-testid="ecosystem-apply-loan-btn"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-extrabold tracking-tight transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:-translate-y-[2px]"
          >
            Get pre-approved at 10.45 %
            <ArrowRight className="h-5 w-5" />
          </button>
          <a
            href="#how-it-works"
            data-testid="ecosystem-how-it-works-btn"
            onClick={(e) => {
              const el = document.getElementById('how-it-works');
              if (el) {
                e.preventDefault();
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            className="inline-flex items-center justify-center gap-1.5 px-6 py-4 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold transition-all"
          >
            How it works
            <ChevronRight className="h-4 w-4" />
          </a>
          <div className="flex-1 sm:text-right">
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
              <RefreshCcw className="h-3 w-3" />
              Bundle benefits available exclusively for Wisedrive loan customers.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OwnershipEcosystem;
